import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Text, TextInput, View } from 'react-native'

import { useQuoteData } from '@flows-ui/services/QuoteDataContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { EVENTS } from '@flows/configs/analytics/constants'
import { getProductParamPath } from '@flows/insurance/utils'
import InfoButton from '@ui-kit/components/InfoButton'
import Spacer from '@ui-kit/components/layoutComponents/Spacer'
import Stack from '@ui-kit/components/layoutComponents/Stack'
import { body2, h3 } from '@ui-kit/core/typography'

import { usePriceChangeTracking } from './hooks'

const DEFAULT_SQUARE_METER_PRICE = 650
const MINIMUM_INSURED_SUM_VALUE = 10_000
const PRODUCT_KEY = 'contents_v1_getsafe_de'

type Props = {
  showForFlowNames?: string[]
}

export const InsuredSumInput = (props: Props) => {
  const { flowName } = useQuoteData()
  const allowedFlowNames = props.showForFlowNames ?? ['contents', 'liability-contents']

  if (!allowedFlowNames.includes(flowName)) {
    return null
  }

  return <InsuredSumInputField />
}

const InsuredSumInputField = () => {
  const { watch, control } = useFormContext()
  const { t } = useTranslation()
  const { trackEvent } = useQuoteData()
  const { trackPriceChange } = usePriceChangeTracking()

  const insuredSumPath = getProductParamPath(PRODUCT_KEY, 'insured_sum_amount')
  const livingSpacePath = getProductParamPath(PRODUCT_KEY, 'living_space_square_meters')

  const { field } = useController({ control, name: insuredSumPath })

  const insuredSumValue = watch(insuredSumPath) ?? 0
  const livingSpaceValue = watch(livingSpacePath) ?? 0

  const recommendedInsuredSum = Number.parseInt(
    String(livingSpaceValue * DEFAULT_SQUARE_METER_PRICE)
  )

  return (
    <View className="pt-12">
      <View className="flex-row items-center justify-between gap-x-2.5">
        <Text className={`flex-1 ${h3()}`}>
          {t('sharedFlows_common:steps.quote.insuredSum.title')}
        </Text>
        <InfoButton
          type="info"
          onOpen={() => trackEvent(EVENTS.PopupOpened, { id: 'insured_sum_info' })}
          modalTitle={t('sharedFlows_common:steps.quote.insured_amount.infobox.title')}
          modalContent={
            <Stack space="small">
              <Text className={body2()}>
                {t('sharedFlows_common:steps.quote.insured_amount.infobox.text1')}
              </Text>
              <Text className={body2()}>
                {t('sharedFlows_common:steps.quote.insured_amount.infobox.text2')}
              </Text>
              <Text className={body2()}>
                {t('sharedFlows_common:steps.quote.insured_amount.infobox.text3')}
              </Text>
            </Stack>
          }
        />
      </View>
      <Spacer size="small" />
      <TextInput
        placeholder={t('sharedFlows_contentsNxt:insured_sum.input.label')}
        value={String(field.value ?? '')}
        onChangeText={(text) => {
          const numValue = Number.parseInt(text) || undefined
          field.onChange(numValue)
          trackPriceChange('insured_sum_change')
        }}
        keyboardType="numeric"
        className="rounded-lg border border-grey-300 px-4 py-3 text-base"
      />
      {insuredSumValue < recommendedInsuredSum && insuredSumValue >= MINIMUM_INSURED_SUM_VALUE && (
        <View className="border-yellow-400 bg-yellow-50 mt-3 rounded-lg border p-4">
          <Text className={body2({ color: 'deepForest' })}>
            {t('sharedFlows_common:steps.quote.underinsured.title')}
          </Text>
          <Text className={body2({ color: 'grey700' })}>
            {t('sharedFlows_common:steps.quote.underinsured.text')}
          </Text>
        </View>
      )}
    </View>
  )
}
